/**
 * Build the player's starter PokemonInstance at new-game time.
 *
 * Faithful to the real formulas (core/stats + core/leveling) so the starter is a
 * proper owned mon from the first second: correct stats for its level, real EXP for
 * the curve, its most-recent level-up moves with real PP, full HP, neutral
 * friendship, and the player as original trainer. Randomness (IVs / nature / gender)
 * flows through an injected core Rng so a pinned seed yields a reproducible starter.
 */
import type { SpeciesData, MoveData, PokemonInstance, MoveSlot, IVs, EVs, StatKey } from '@/shared';
import { STAT_KEYS, LEVEL_MAX } from '@/shared';
import type { Rng } from '@/core';
import { computeStats, NATURES } from '@/core';
import { expForLevel } from '@/core';

const NEUTRAL_MON_FRIENDSHIP = 70;

function rollIvs(rng: Rng): IVs {
  const out = {} as Record<StatKey, number>;
  for (const k of STAT_KEYS) out[k] = rng.int(32); // 0..31
  return out as IVs;
}

const ZERO_EVS: EVs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };

/** Resolve the up-to-4 most-recent level-up moves a species knows by `level`. */
function starterMoves(
  species: SpeciesData,
  level: number,
  getMove: (id: string) => MoveData | undefined,
): MoveSlot[] {
  const learnable = species.learnset
    .filter((e) => e.method === 'level-up' && (e.level ?? 1) <= level)
    .sort((a, b) => (a.level ?? 1) - (b.level ?? 1));
  const chosen = learnable.slice(-4);
  const list = chosen.length > 0 ? chosen : species.learnset.slice(0, 1);

  const slots: MoveSlot[] = [];
  for (const e of list) {
    const md = getMove(e.moveId);
    const maxPp = md?.pp ?? 5;
    slots.push({ moveId: e.moveId, pp: maxPp, maxPp });
  }
  // A mon with no learnset at all still needs a slot so battles never break.
  if (slots.length === 0) slots.push({ moveId: 'tackle', pp: 35, maxPp: 35 });
  return slots;
}

export interface StarterOptions {
  readonly species: SpeciesData;
  readonly level: number;
  readonly playerName: string;
  readonly rng: Rng;
  readonly getMove: (id: string) => MoveData | undefined;
  /** Wall-clock ms the starter was obtained. */
  readonly now: number;
}

/** Construct the player's starter as a fully-formed, faithful owned instance. */
export function makeStarter(opts: StarterOptions): PokemonInstance {
  const { species, playerName, rng, getMove, now } = opts;
  const level = Math.max(1, Math.min(LEVEL_MAX, Math.trunc(opts.level)));

  const ivs = rollIvs(rng);
  const nature = rng.pick(NATURES);
  const stats = computeStats(species.baseStats, ivs, ZERO_EVS, level, nature);

  let gender: 'male' | 'female' | null = null;
  if (species.genderRatio !== null) {
    gender = rng.float() < species.genderRatio ? 'female' : 'male';
  }

  return {
    id: `player-starter-${species.id}-${rng.getState().toString(36)}`,
    speciesId: species.id,
    nickname: null,
    level,
    exp: expForLevel(species.growthRate, level),
    natureId: nature.id,
    ivs,
    evs: { ...ZERO_EVS },
    abilityId: species.abilities.primary,
    moves: starterMoves(species, level, getMove),
    currentHp: stats.hp,
    maxHp: stats.hp,
    status: 'none',
    statusTurns: 0,
    heldItemId: null,
    friendship: NEUTRAL_MON_FRIENDSHIP,
    shiny: false,
    gender,
    originalTrainer: { trainerId: 'player', trainerName: playerName, viaTrade: false },
    obtainedAt: now,
  };
}
